if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Perlehaus — blurred-matte fallback for oddly-shaped product photos.
        // Dawn's constrain-height + media-fit-contain layout narrows the media
        // box to the image's own aspect ratio, leaving empty space in the wider
        // slide slot. Instead of showing a flat background color there, fill it
        // with a blurred, scaled copy of the same photo so it always blends with
        // that specific image's own colors, regardless of background.
        this.setupBlurFill();

        // Perlehaus — mobile overlay pagination dot sync (product-media-
        // gallery.liquid renders one .product__mobile-dot per real slide,
        // styled/positioned by perlehaus-tokens.css). Deliberately called
        // BEFORE the `!this.elements.thumbnails` early-return below: on
        // mobile (mobile_thumbnails: "hide") there is no thumbnail rail,
        // so that early-return would otherwise skip this entirely and the
        // dots would never update. Independent of the thumbnail-sync
        // logic below, both driven by the same slider-component
        // `slideChanged` event (global.js SliderComponent.update()).
        this.setupMobileDots();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      setupMobileDots() {
        const dots = this.querySelector('.product__mobile-dots');
        if (!dots) return;
        // Query fresh each time rather than capturing dotEls once: a
        // variant change (product-info.js updateMedia) replaces this
        // container's innerHTML entirely when the real slide count
        // differs between colours, which would otherwise leave this
        // listener holding a stale reference to detached nodes.
        const getDotEls = () => Array.from(dots.querySelectorAll('.product__mobile-dot'));
        const initialDotEls = getDotEls();
        if (!initialDotEls.length) return;

        // SliderComponent (global.js) only fires `slideChanged` on scroll,
        // and its very first firing happens during slider-component's own
        // construction -- which runs before media-gallery.js has executed
        // (global.js is loaded first), so that initial event is always
        // missed. Slide 1 is always the initially-visible slide regardless
        // (matching Dawn's own `is-active`-on-first-item convention), so
        // set it directly instead of waiting on an event that already fired.
        initialDotEls[0].classList.add('is-current');

        this.elements.viewer.addEventListener('slideChanged', (event) => {
          const currentPage = event.detail.currentPage;
          getDotEls().forEach((dot, index) => {
            dot.classList.toggle('is-current', index === currentPage - 1);
          });
        });
      }

      setupBlurFill() {
        this.querySelectorAll('.product-media-container.constrain-height.media-fit-contain').forEach((container) => {
          const item = container.closest('.product__media-item');
          if (!item || item.querySelector('.media-blur-fill')) return;
          const img = container.querySelector('img');
          if (!img) return;

          const applyFill = () => {
            const src = img.currentSrc || img.src;
            if (!src) return;
            // Only for genuinely non-square images — see media-blur-fill.liquid
            // for why an unconditional fill created a visible gray band on
            // square images that share a taller gallery slot with siblings.
            const ratio = img.naturalWidth / img.naturalHeight;
            if (ratio >= 0.9 && ratio <= 1.1) return;
            item.classList.add('has-blur-fill');
            const fill = document.createElement('div');
            fill.className = 'media-blur-fill';
            fill.style.backgroundImage = `url("${src}")`;
            fill.setAttribute('aria-hidden', 'true');
            fill.textContent = '\u00A0'; // defeat the theme's `div:empty { display: none }` reset
            item.prepend(fill);
          };

          if (img.complete && img.naturalWidth) {
            applyFill();
          } else {
            img.addEventListener('load', applyFill, { once: true });
          }
        });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
